import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "list", "count", "hiddenInputs", "error", "submit"]
  static values = { url: String, existing: Array }

  connect() {
    this.photos = this.existingValue.map(photo => ({ ...photo }))
    this.busy = false
    this.render()
  }

  select(event) {
    const files = Array.from(event.target.files)
    event.target.value = ""
    this.upload(files)
  }

  dragover(event) { event.preventDefault() }
  drop(event) {
    event.preventDefault()
    this.upload(Array.from(event.dataTransfer?.files || []))
  }

  async upload(files) {
    if (this.busy || files.length === 0) return
    this.errorTarget.textContent = ""
    if (this.photos.length + files.length > 10) return this.showError("기존 사진을 포함해 최대 10장까지 등록할 수 있습니다.")
    if (files.some(file => !["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type))) {
      return this.showError("JPG, PNG, GIF, WebP 사진만 선택해주세요.")
    }
    if (files.some(file => file.size === 0 || file.size > 10 * 1024 * 1024)) return this.showError("사진은 장당 최대 10MB입니다.")

    this.setBusy(true)
    for (const file of files) {
      const preview = { key: `pending:${crypto.randomUUID()}`, url: URL.createObjectURL(file), filename: file.name, pending: true }
      this.photos.push(preview)
      this.render()
      const form = new FormData()
      form.append("file", file)
      form.append("upload_token", this.element.querySelector('[name="upload_token"]').value)
      form.append("retained_file_ids[]", "")
      this.photos.filter(photo => photo.key.startsWith("existing:")).forEach(photo => form.append("retained_file_ids[]", photo.bf_no))
      try {
        const response = await fetch(this.urlValue, { method: "POST", headers: this.headers(), body: form })
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || "사진 업로드에 실패했습니다.")
        this.photos.splice(this.photos.indexOf(preview), 1, data)
      } catch (error) {
        this.photos = this.photos.filter(photo => photo !== preview)
        this.showError(`${file.name}: ${error.message}`)
      } finally {
        URL.revokeObjectURL(preview.url)
        this.render()
      }
    }
    this.setBusy(false)
    this.render()
  }

  async remove(index) {
    if (this.busy) return
    const photo = this.photos[index]
    if (photo.delete_url) {
      this.setBusy(true)
      try {
        const response = await fetch(photo.delete_url, { method: "DELETE", headers: this.headers() })
        if (!response.ok) throw new Error("사진 삭제에 실패했습니다. 다시 시도해주세요.")
      } catch (error) {
        this.showError(error.message)
        return
      } finally {
        this.setBusy(false)
      }
    }
    this.photos.splice(index, 1)
    this.render()
  }

  move(from, to) {
    if (this.busy || to < 0 || to >= this.photos.length) return
    this.photos.splice(to, 0, this.photos.splice(from, 1)[0])
    this.render()
  }

  validate(event) {
    if (this.busy || this.photos.length < 1 || this.photos.length > 10) {
      event.preventDefault()
      this.showError(this.busy ? "사진 업로드가 끝날 때까지 기다려주세요." : "상품 사진을 1장 이상, 최대 10장 등록해주세요.")
    }
  }

  render() {
    this.listTarget.replaceChildren()
    this.hiddenInputsTarget.replaceChildren()
    this.hiddenInput("")
    this.photos.forEach((photo, index) => {
      const card = document.createElement("div")
      card.className = "overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
      const img = document.createElement("img")
      img.src = photo.url
      img.alt = photo.filename
      img.className = "aspect-square w-full object-cover"
      card.append(img)
      const label = document.createElement("p")
      label.className = "px-2 pt-2 text-xs font-medium truncate"
      label.textContent = photo.pending ? "업로드 중…" : index === 0 ? "대표사진" : `사진 ${index + 1}`
      card.append(label)
      const actions = document.createElement("div")
      actions.className = "flex flex-wrap gap-1 p-2"
      const button = (text, title, handler, disabled = false) => {
        const element = document.createElement("button")
        element.type = "button"
        element.textContent = text
        element.setAttribute("aria-label", `${index + 1}번 사진 ${title}`)
        element.className = "rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs disabled:opacity-40"
        element.disabled = this.busy || disabled
        element.addEventListener("click", handler)
        actions.append(element)
      }
      button("←", "앞으로 이동", () => this.move(index, index - 1), index === 0)
      button("→", "뒤로 이동", () => this.move(index, index + 1), index === this.photos.length - 1)
      button("대표", "대표사진으로 선택", () => this.move(index, 0), index === 0)
      button("삭제", "삭제", () => this.remove(index))
      card.append(actions)
      this.listTarget.append(card)
      if (!photo.pending) this.hiddenInput(photo.key)
    })
    this.countTarget.textContent = `${this.photos.length} / 10`
    this.existingValue = this.photos.filter(photo => !photo.pending)
  }

  hiddenInput(value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "photo_ids[]"
    input.value = value
    this.hiddenInputsTarget.append(input)
  }

  setBusy(busy) {
    this.busy = busy
    this.submitTarget.disabled = busy
    this.inputTarget.disabled = busy
  }

  showError(message) { this.errorTarget.textContent = message }
  headers() { return { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", Accept: "application/json" } }
}
